import React, { useEffect, useState } from "react";
import { fireStore } from "../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/date.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const Farmers = () => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 1);

  const [startDate, setStartDate] = useState(threeDaysAgo.getTime()); // Store as milliseconds
  const [endDate, setEndDate] = useState(Date.now()); // Store as milliseconds

  const [Farmers, setFarmers] = useState([]);
  const [filteredFarmers, setFilteredFarmers] = useState([]);
  const [totalFarmers, setTotalFarmers] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState("");
  const [territories, setTerritories] = useState([]);

  //   Fetch Farmers...
  useEffect(() => {
    const fetchFarmers = async () => {
      try {
        const collectionRef = collection(fireStore, "Farmers");

        // Firestore Query (Assumes 'timestamp' field exists)
        const q = query(
          collectionRef,
          where("id", ">=", startDate),
          where("id", "<=", endDate)
        );

        const querySnapshot = await getDocs(q);

        // Extract and format data
        const farmersList = querySnapshot.docs.map((doc) => ({
          id: doc.id, // Keep Firestore document ID
          ...doc.data(), // Spread document data
        }));

        setFarmers(farmersList);
        setFilteredFarmers(farmersList);
        setTotalFarmers(farmersList.length);
      } catch (error) {
        console.error("Error fetching Farmers:", error);
      }
    };

    fetchFarmers();
  }, [startDate, endDate]); // Depend on date changes

  //   Fetch Territories...
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const collectionRef = collection(fireStore, "Territories");
        const querySnapshot = await getDocs(collectionRef);
        const territoriesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort territories in ascending order based on a property, e.g., 'name'
        const sortedTerritories = territoriesList.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        setTerritories(sortedTerritories);
      } catch (error) {
        console.error("Error fetching territories:", error);
      }
    };

    fetchTerritories();
  }, []);

  //   Export Farmers...
  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const margin = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const container = document.getElementById("FarmersContainer");
    const originalDisplay = container.style.display;
    container.style.display = "block";
    await new Promise((r) => setTimeout(r, 50)); // quicker render wait

    const fullCanvas = await html2canvas(container, {
      scale: 1,
      useCORS: true,
    });
    const imgWidth = pageWidth - 2 * margin;
    const scaleFactor = imgWidth / fullCanvas.width;
    const sliceHeight = (pageHeight - 2 * margin) / scaleFactor;
    const totalPages = Math.ceil(fullCanvas.height / sliceHeight);

    for (let i = 0; i < totalPages; i++) {
      if (i) doc.addPage();

      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = fullCanvas.width;
      sliceCanvas.height = sliceHeight;
      sliceCanvas
        .getContext("2d")
        .drawImage(
          fullCanvas,
          0,
          i * sliceHeight,
          fullCanvas.width,
          sliceHeight,
          0,
          0,
          fullCanvas.width,
          sliceHeight
        );

      const imgData = sliceCanvas.toDataURL("image/jpeg", 0.6);
      doc.addImage(
        imgData,
        "JPEG",
        margin,
        margin,
        imgWidth,
        sliceHeight * scaleFactor
      );
    }

    doc.save("Farmers.pdf");
    container.style.display = originalDisplay;
  };

  // Filters
  useEffect(() => {
    const filtered = Farmers.filter(
      (visit) =>
        visit.user.territory.name.toLowerCase() ===
        selectedTerritory.toLocaleLowerCase()
    );

    setFilteredFarmers(filtered);
  }, [selectedTerritory]);

  // Filter Logbooks...
  useEffect(() => {
    const filtered = Farmers.filter((visit) =>
      visit.user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredFarmers(filtered);
    if (searchQuery === "") {
      setFilteredFarmers(Farmers);
    }
  }, [searchQuery]);

  return (
    <div className="p-4 bg-lightgray h-full overflow-y-scroll">
      <div className="bg-white p-4 rounded-lg shadow-lg max-w-5xl mx-auto lg:sticky top-0 ">
        <div className="flex justify-between items-center">
          <div className="flex gap-4">
            <h1 className="text-xl font-semibold py-4 text-liquidblue">
              Farmers ({filteredFarmers.length})
            </h1>
          </div>

          <button
            onClick={exportToPDF}
            className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-4 py-1"
          >
            PDF
          </button>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-2 space-y-2 md:space-y-0">
          <select
            id="territories"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            onChange={(e) => setSelectedTerritory(e.target.value)}
            value={selectedTerritory}
          >
            <option value="">Select Territory</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          <div className="flex items-center ">
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              id="from-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(startDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setStartDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 space-y-2 md:space-y-0">
          <input
            type="text"
            placeholder="Search"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center ">
            <label
              htmlFor="to-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              To Date
            </label>
            <input
              type="date"
              id="to-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(endDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setEndDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>
      </div>

      {filteredFarmers.map((item) => (
        <div
          key={item.id}
          className="overflow-x-auto bg-white p-4 rounded-lg mt-4"
        >
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-semibold py-4 text-liquidblue">
              {item.user?.territory?.name}
            </h1>
          </div>
          <div className="flex flex-wrap justify-between items-center p-2">
            <div className="w-full md:w-auto flex-1 mb-2 md:mb-0 text-center md:text-left">
              <h2 className="font-bold text-sm md:text-base">
                {item.user?.name}
              </h2>
            </div>
            <div className="w-full md:w-auto flex-1 mb-2 md:mb-0 text-center md:text-left">
              <h2 className="font-bold text-sm md:text-base">
                {item.user?.phone}
              </h2>
            </div>
            <div className="w-full md:w-auto flex-1 mb-2 md:mb-0 text-center md:text-left">
              <p className="text-sm md:text-base">
                {new Date(Number(item.id)).toLocaleDateString("en-GB")}{" "}
                {new Date(Number(item.id)).toLocaleTimeString("en-GB", {
                  hour12: true,
                })}{" "}
              </p>
            </div>
          </div>
          <table className="min-w-full bg-white mt-2">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b border-gray-200 text-black text-xs md:text-sm">
                  Farmer Image
                </th>
                <th className="py-2 px-4 border-b border-gray-200 text-black text-xs md:text-sm">
                  Farmer Name
                </th>
                <th className="py-2 px-4 border-b border-gray-200 text-black text-xs md:text-sm">
                  Father Name
                </th>
                <th className="py-2 px-4 border-b border-gray-200 text-black text-xs md:text-sm">
                  Mobile Number
                </th>
                <th className="py-2 px-4 border-b border-gray-200 text-black text-xs md:text-sm">
                  Cast
                </th>
                <th className="py-2 px-4 border-b border-gray-200 text-black text-xs md:text-sm">
                  Acres
                </th>
                <th className="py-2 px-4 border-b border-gray-200 text-black text-xs md:text-sm">
                  Address
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4">
                  <img
                    src={item.picture}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-full"
                  />
                </td>
                <td className="py-2 px-4 text-xs md:text-sm">{item.name}</td>
                <td className="py-2 px-4 text-xs md:text-sm">
                  {item.fatherName}
                </td>
                <td className="py-2 px-4 text-xs md:text-sm">{item.phone}</td>
                <td className="py-2 px-4 text-xs md:text-sm">{item.cast}</td>
                <td className="py-2 px-4 text-xs md:text-sm">{item.acres}</td>
                <td className="py-2 px-4 text-xs md:text-sm">{item.address}</td>
              </tr>
            </tbody>
          </table>

          <br></br>
          <br></br>
        </div>
      ))}

      <div id="FarmersContainer" class="bg-gray-100 hidden">
        <div class="container mx-auto py-8">
          <div class="flex justify-between items-center mb-4">
            <div class="text-darkteal font-semibold flex gap-4">
              <h3>Registered Farmers</h3>
              <h3>{totalFarmers}</h3>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="min-w-full bg-white border border-darkteal">
              <thead>
                <tr>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Sr. No.
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Farmer Name
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Father Name
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Mobile Number
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Cast
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Total Acres
                  </th>
                  <th class="px-4 py-2 border border-darkteal bg-darkteal text-white">
                    Postal Address
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFarmers.map((item, index) => (
                  <tr key={item.id}>
                    <td class="px-4 py-2 border border-darkteal text-center">
                      {index + 1}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.name}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.fatherName}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.phone}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.cast}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.acres}
                    </td>
                    <td class="px-4 py-2 border border-darkteal">
                      {item.address}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Farmers;
